/**
 * Cloud V2 — Cart Drawer
 * Barra lateral do carrinho, 100% JS próprio (fetch + innerHTML), sem
 * depender de assets/global.js (nunca carregado neste tema) nem dos
 * custom elements nativos do Dawn (<quantity-input>, <cart-remove-button>).
 * Reage a: clique no ícone do carrinho do header, alterar quantidade,
 * remover item, e ao evento "cloud:cart:added" disparado pelo CloudCart
 * (assets/cloud.js) depois de um "Adicionar ao carrinho" bem-sucedido.
 */
(function () {
  'use strict';

  const FREE_SHIPPING_THRESHOLD_CENTS = 8990; // €89,90 — mesma regra da barra de aviso
  const PROMO_ELIGIBLE_THRESHOLD_CENTS = 3000; // €30,00 — mesma regra da promoção "Compre 2 Leve 3"

  function formatMoney(cents) {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
    return new Intl.NumberFormat('pt-PT', { style: 'currency', currency }).format(cents / 100);
  }

  const CloudCartDrawer = {
    init() {
      this.drawer = document.getElementById('cloud-cart-drawer');
      this.backdrop = document.getElementById('cloud-cart-drawer-backdrop');
      this.closeBtn = document.getElementById('cloud-cart-drawer-close');
      this.body = document.getElementById('cloud-cart-drawer-body');
      this.trigger = document.getElementById('cart-icon-bubble');

      if (!this.drawer) return;

      if (this.trigger) {
        this.trigger.setAttribute('role', 'button');
        this.trigger.setAttribute('aria-haspopup', 'dialog');
        this.trigger.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      }

      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
      if (this.backdrop) this.backdrop.addEventListener('click', () => this.close());

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer.classList.contains('cloud-cart-drawer--open')) this.close();
      });

      // Delegação de eventos: os itens são re-renderizados via innerHTML,
      // por isso os listeners ficam no contentor, não em cada botão.
      this.body.addEventListener('click', (e) => {
        const minusBtn = e.target.closest('[data-cart-qty-minus]');
        const plusBtn = e.target.closest('[data-cart-qty-plus]');
        const removeBtn = e.target.closest('[data-cart-remove]');

        if (minusBtn) this.changeLineByDelta(minusBtn.dataset.line, -1);
        if (plusBtn) this.changeLineByDelta(plusBtn.dataset.line, 1);
        if (removeBtn) this.setLineQuantity(removeBtn.dataset.line, 0);
      });

      document.addEventListener('cloud:cart:added', () => {
        this.refresh().then(() => this.open());
      });
    },

    open() {
      this.drawer.classList.add('cloud-cart-drawer--open');
      document.body.classList.add('cloud-cart-drawer-lock');
      this.refresh();
    },

    close() {
      this.drawer.classList.remove('cloud-cart-drawer--open');
      document.body.classList.remove('cloud-cart-drawer-lock');
    },

    async changeLineByDelta(line, delta) {
      const item = this.currentCart ? this.currentCart.items[line - 1] : null;
      const currentQty = item ? item.quantity : 1;
      await this.setLineQuantity(line, Math.max(0, currentQty + delta));
    },

    async setLineQuantity(line, quantity) {
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: Number(line), quantity: Number(quantity) })
        });
        const cart = await res.json();
        this.render(cart);
      } catch (err) {
        console.warn('Cloud cart drawer error:', err);
      }
    },

    async refresh() {
      try {
        const res = await fetch('/cart.js');
        const cart = await res.json();
        this.render(cart);
      } catch (err) {
        console.warn('Cloud cart drawer error:', err);
      }
    },

    render(cart) {
      this.currentCart = cart;
      this.drawer.classList.toggle('cloud-cart-drawer--empty', cart.item_count === 0);

      document.querySelectorAll('.cloud-header__cart-count').forEach((badge) => {
        badge.textContent = cart.item_count;
        badge.hidden = cart.item_count === 0;
      });

      if (cart.item_count === 0) {
        this.body.innerHTML = `
          <div class="cloud-cart-drawer__empty">
            <p>O teu carrinho está vazio.</p>
            <a href="${window.Shopify.routes.root}collections/all" class="cloud-btn cloud-btn--primary">Continuar a Comprar</a>
          </div>`;
        const footer = document.getElementById('cloud-cart-drawer-footer');
        if (footer) footer.innerHTML = '';
        return;
      }

      this.body.innerHTML = `<div class="cloud-cart-drawer__items" id="cloud-cart-drawer-items">${cart.items
        .map((item, i) => this.renderItem(item, i + 1))
        .join('')}</div>`;

      let footer = document.getElementById('cloud-cart-drawer-footer');
      if (!footer) {
        footer = document.createElement('div');
        footer.className = 'cloud-cart-drawer__footer';
        footer.id = 'cloud-cart-drawer-footer';
        this.drawer.querySelector('.cloud-cart-drawer__panel').appendChild(footer);
      }
      footer.innerHTML = this.renderFooter(cart);
    },

    renderItem(item, line) {
      const hasDiscount = item.original_line_price !== item.final_line_price;
      const discountPct = hasDiscount
        ? Math.round(((item.original_line_price - item.final_line_price) / item.original_line_price) * 100)
        : 0;
      const variantLabel =
        item.variant_title && item.variant_title !== 'Default Title'
          ? `<p class="cloud-cart-item__variant">(${item.variant_title.replace(/ \/ /g, ', ')})</p>`
          : '';
      // item.image do /cart.js já vem como URL completa do CDN; inserir o
      // sufixo de tamanho do Shopify (_200x) antes da extensão evita
      // descarregar a imagem no tamanho original só para mostrar 80x100px.
      const resizedSrc = item.image
        ? item.image.replace(/\.(jpg|jpeg|png|gif|webp)(\?|$)/i, '_200x.$1$2')
        : '';
      const image = item.image
        ? `<img src="${resizedSrc}" alt="${(item.product_title || '').replace(/"/g, '&quot;')}" loading="lazy" width="80" height="80">`
        : '';

      return `
        <div class="cloud-cart-item" data-line="${line}">
          <a href="${item.url}" class="cloud-cart-item__image" tabindex="-1" aria-hidden="true">${image}</a>
          <div class="cloud-cart-item__body">
            <div class="cloud-cart-item__top">
              <div class="cloud-cart-item__info">
                <a href="${item.url}" class="cloud-cart-item__name">${item.product_title}</a>
                ${variantLabel}
              </div>
              <button type="button" class="cloud-cart-item__remove" data-cart-remove data-line="${line}" aria-label="Remover ${item.product_title}">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M3 6h18"/>
                  <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                </svg>
              </button>
            </div>
            <div class="cloud-cart-item__bottom">
              <div class="cloud-quantity cloud-cart-item__qty" data-line="${line}">
                <button type="button" class="cloud-quantity__btn" data-cart-qty-minus data-line="${line}" aria-label="Diminuir quantidade">−</button>
                <span class="cloud-cart-item__qty-value">${item.quantity}</span>
                <button type="button" class="cloud-quantity__btn" data-cart-qty-plus data-line="${line}" aria-label="Aumentar quantidade">+</button>
              </div>
              <div class="cloud-cart-item__price">
                ${hasDiscount
                  ? `<span class="cloud-cart-item__price-original">${formatMoney(item.original_line_price)}</span>
                     <span class="cloud-cart-item__price-badge">-${discountPct}%</span>
                     <span class="cloud-cart-item__price-final">${formatMoney(item.final_line_price)}</span>`
                  : `<span class="cloud-cart-item__price-final">${formatMoney(item.final_line_price)}</span>`
                }
              </div>
            </div>
          </div>
        </div>`;
    },

    renderFooter(cart) {
      const truckIcon = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M1 3h15v13H1z"/>
        <path d="M16 8h4l3 3v5h-7V8z"/>
        <circle cx="5.5" cy="18.5" r="2.5"/>
        <circle cx="18.5" cy="18.5" r="2.5"/>
      </svg>`;
      const giftIcon = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <rect x="3" y="8" width="18" height="4"/>
        <path d="M12 8v13"/>
        <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/>
        <path d="M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8s1-5 4.5-5a2.5 2.5 0 0 1 0 5"/>
      </svg>`;

      const qualifies = cart.total_price >= FREE_SHIPPING_THRESHOLD_CENTS;
      const remaining = FREE_SHIPPING_THRESHOLD_CENTS - cart.total_price;
      const shippingText = qualifies
        ? 'Frete grátis garantido!'
        : `Faltam ${formatMoney(remaining)} para o frete grátis`;

      const promoEligibleQty = cart.items
        .filter((item) => item.price <= PROMO_ELIGIBLE_THRESHOLD_CENTS)
        .reduce((sum, item) => sum + item.quantity, 0);
      const promoFreeCount = Math.floor(promoEligibleQty / 3);
      const promoRemainder = promoEligibleQty % 3;
      const promoNeeded = promoRemainder > 0 ? 3 - promoRemainder : 0;
      let promoMsg = '';
      if (promoEligibleQty > 0) {
        const promoText = promoFreeCount > 0
          ? `Compre 2 Leve 3 ativado — ${promoFreeCount} produto${promoFreeCount > 1 ? 's' : ''} grátis!${promoNeeded > 0 ? ` Faltam ${promoNeeded} para o próximo grátis!` : ''}`
          : `Faltam ${promoNeeded} produto${promoNeeded > 1 ? 's' : ''} até €30 para o próximo grátis (Compre 2 Leve 3)!`;
        promoMsg = `<div class="cloud-cart-drawer__shipping-msg${promoFreeCount > 0 ? ' cloud-cart-drawer__shipping-msg--qualified' : ''}">${giftIcon}<span>${promoText}</span></div>`;
      }

      return `
        <div class="cloud-cart-drawer__shipping-msg${qualifies ? ' cloud-cart-drawer__shipping-msg--qualified' : ''}">${truckIcon}<span>${shippingText}</span></div>
        ${promoMsg}

        <div class="cloud-cart-drawer__row cloud-cart-drawer__row--subtotal">
          <span>Subtotal (sem frete):</span>
          <span class="cloud-cart-drawer__row-amount">${formatMoney(cart.total_price)}</span>
        </div>

        <div class="cloud-cart-drawer__row cloud-cart-drawer__row--total">
          <span>Total:</span>
          <span class="cloud-cart-drawer__row-amount">${formatMoney(cart.total_price)}</span>
        </div>

        <button type="button" class="cloud-btn cloud-btn--primary cloud-btn--lg cloud-cart-drawer__checkout" id="cloud-cart-drawer-checkout">
          Iniciar Compra
        </button>

        <a href="${window.Shopify.routes.root}collections/all" class="cloud-cart-drawer__continue">Ver mais produtos</a>`;
    }
  };

  // O botão "Iniciar Compra" é recriado a cada render() — delegar o clique
  // no drawer inteiro em vez de o religar toda a vez.
  document.addEventListener('click', (e) => {
    if (e.target.closest('#cloud-cart-drawer-checkout')) {
      window.location.href = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/') + 'checkout';
    }
  });

  function init() {
    CloudCartDrawer.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
